import { useState } from 'react';
import { NewsArticle } from '../page';

interface NewsContentProps {
  news: NewsArticle;
}

export default function NewsContent({ news }: NewsContentProps) {
  const [copied, setCopied] = useState(false);

  const dateStr = news.published_at
    ? new Date(news.published_at).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })
    : '';

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = news.title;
    if (platform === 'weibo') {
      window.open(
        `https://service.weibo.com/share/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
        '_blank'
      );
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <section className="py-10 md:py-14 lg:py-16 bg-white">
      <div className="max-w-4xl mx-auto px-5 md:px-6 lg:px-8">
        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 md:gap-6 pb-6 md:pb-8 border-b border-gray-100 mb-8 md:mb-10">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-full">
              <i className="ri-user-line text-teal-600 text-sm"></i>
            </div>
            <span>{news.author || '编辑部'}</span>
          </div>
          {dateStr && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-line text-gray-400"></i>
              </div>
              <span>{dateStr}</span>
            </div>
          )}
          {news.read_time && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-time-line text-gray-400"></i>
              </div>
              <span>阅读约 {news.read_time}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-eye-line text-gray-400"></i>
            </div>
            <span>{(news.view_count ?? 0).toLocaleString()} 次阅读</span>
          </div>
        </div>

        {/* Summary */}
        {news.summary && (
          <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-lg px-5 py-4 mb-8 md:mb-10">
            <p className="text-sm md:text-base text-teal-900 leading-relaxed font-medium">{news.summary}</p>
          </div>
        )}

        {/* Article Content */}
        {news.content ? (
          <div
            className="prose-article"
            dangerouslySetInnerHTML={{ __html: news.content }}
          />
        ) : (
          <p className="text-gray-500 text-center py-10">暂无文章内容</p>
        )}

        {/* Tags */}
        {(news.tags ?? []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 md:mt-12 pt-8 border-t border-gray-100">
            <span className="text-sm text-gray-500 mr-1">标签：</span>
            {news.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Share */}
        <div className="flex flex-wrap items-center gap-3 mt-6 pt-6 border-t border-gray-100">
          <span className="text-sm text-gray-500">分享至：</span>
          <button
            onClick={() => handleShare('weibo')}
            className="flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-500 text-xs rounded-full hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-weibo-fill"></i>
            微博
          </button>
          <button
            onClick={() => handleShare('copy')}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-links-line"></i>
            {copied ? '已复制！' : '复制链接'}
          </button>
        </div>
      </div>
    </section>
  );
}
